import type { LoginSessionUser, MfaLevel, SessionUser } from "@/lib/api/session";
import { BRANCHES, MOCK_COMPANY_ID } from "@/mocks/fixtures/branches";

/**
 * In-memory state backing the MSW handlers. Module-level and mutable on
 * purpose — tests call reset() between cases.
 */

export interface MockUser {
  id: string;
  code: string;
  /** Plaintext — mock data only, never a production pattern. */
  password: string;
  display_name: string;
  permissions: string[];
  mfa_level: MfaLevel;
  company_id: string;
  default_branch_id: string | null;
  allowed_branch_ids: string[];
}

export const DEMO_USER: MockUser = {
  id: "01912f7e-9c3a-7d10-8b4e-2f6a1c0d5e93",
  code: "0001",
  password: "Passw0rd!demo123",
  display_name: "Demo Admin",
  company_id: MOCK_COMPANY_ID,
  default_branch_id: BRANCHES[0]?.id ?? null,
  allowed_branch_ids: BRANCHES.filter((branch) => branch.status === "active").map(
    (branch) => branch.id,
  ),
  // UNION GRANT (permission-flip bridge, HANDOFFS 2026-08-10): legacy demo
  // names AND every ratified x-required-permission from canonical+draft specs,
  // so either lane's rename lands without hiding nav. Legacy names drop when
  // CODEX-1 seeds real RBAC.
  permissions: [
    "catalog.attribute.create",
    "catalog.attribute.delete",
    "catalog.attribute.manage",
    "catalog.attribute.read",
    "catalog.attribute.update",
    "catalog.barcode.create",
    "catalog.barcode.delete",
    "catalog.barcode.read",
    "catalog.category.read",
    "catalog.component.create",
    "catalog.component.delete",
    "catalog.component.read",
    "catalog.component.update",
    "catalog.import.commit",
    "catalog.import.create",
    "catalog.import.delete",
    "catalog.import.read",
    "catalog.price_list.create",
    "catalog.price_list.delete",
    "catalog.price_list.read",
    "catalog.price_list.update",
    "catalog.product.create",
    "catalog.product.delete",
    "catalog.product.read",
    "catalog.product.update",
    "catalog.tax_class.create",
    "catalog.tax_class.delete",
    "catalog.tax_class.read",
    "catalog.tax_class.update",
    "catalog.uom.create",
    "catalog.uom.delete",
    "catalog.uom.manage",
    "catalog.uom.read",
    "catalog.uom.update",
    "catalog.variant.create",
    "catalog.variant.delete",
    "catalog.variant.read",
    "catalog.variant.update",
    "channels.order.raw.read",
    "channels.order.read",
    "channels.order.retry",
    "channels.orders.read",
    "channels.reconciliation.read",
    // SPEC-PENDING name from features/connectors/nav.ts — granted under the
    // union-grant policy above so their entry is not invisible meanwhile.
    "channels.source_set.manage",
    "channels.webhook.ingest",
    // Finance — receivables, payables, tax, fixed assets, statements. Every
    // name is an x-required-permission read verbatim from the slice's own
    // permission constants (AR_/AP_/TAX_/ASSET_/STATEMENT_PERMISSIONS), so the
    // whole Finance menu resolves for the demo user in mock mode.
    "finance.ap-aging.read",
    "finance.ar-aging.read",
    "finance.asset-class.create",
    "finance.asset-class.read",
    "finance.asset-class.update",
    "finance.credit-note.create",
    "finance.credit-note.finalize",
    "finance.credit-note.read",
    "finance.customer-invoice.create",
    "finance.customer-invoice.finalize",
    "finance.customer-invoice.read",
    "finance.customer-receipt.allocate",
    "finance.customer-receipt.create",
    "finance.customer-receipt.read",
    "finance.customer-statement.read",
    "finance.customer-statement.run",
    "finance.depreciation-run.execute",
    "finance.depreciation-run.read",
    "finance.dunning.configure",
    "finance.dunning.read",
    "finance.dunning.run",
    "finance.fixed-asset.create",
    "finance.fixed-asset.dispose",
    "finance.fixed-asset.read",
    "finance.fixed-asset.update",
    "finance.grni.read",
    "finance.payment-run.create",
    "finance.payment-run.export",
    "finance.payment-run.post",
    "finance.payment-run.read",
    "finance.payment-run.submit",
    "finance.statement-layout.create",
    "finance.statement-layout.read",
    "finance.statement-layout.update",
    "finance.statement.read",
    "finance.supplier-invoice.create",
    "finance.supplier-invoice.match",
    "finance.supplier-invoice.override-discrepancy",
    "finance.supplier-invoice.post",
    "finance.supplier-invoice.read",
    "finance.supplier-invoice.resolve-discrepancy",
    "finance.tax-code.create",
    "finance.tax-code.read",
    "finance.tax-code.update",
    "finance.tax-return.read",
    "inventory.batch.read",
    "inventory.count.approve",
    "inventory.count.cancel",
    "inventory.count.create",
    "inventory.count.enter",
    "inventory.count.read",
    "inventory.count.submit",
    "inventory.counts.read",
    "inventory.movement.create",
    "inventory.movement.read",
    "inventory.movements.create",
    "inventory.report.export",
    "inventory.report.read",
    "inventory.reservation.consume",
    "inventory.reservation.release",
    "inventory.reservation.reserve",
    "inventory.stock.read",
    "inventory.transfer.cancel",
    "inventory.transfer.create",
    "inventory.transfer.read",
    "inventory.transfer.receive",
    "inventory.transfer.ship",
    "inventory.transfer.update",
    "inventory.warehouse.create",
    "inventory.warehouse.delete",
    "inventory.warehouse.read",
    "inventory.warehouse.update",
    "inventory.warehouses.read",
    "inventory.write-down.post",
    "inventory.write-down.read",
    "ledger.account.create",
    "ledger.account.delete",
    "ledger.account.import",
    "ledger.account.import.preview",
    "ledger.account.read",
    "ledger.account.update",
    "ledger.business-event.read",
    "ledger.business-event.replay",
    "ledger.fiscal-calendar.generate",
    "ledger.fiscal-calendar.read",
    "ledger.fiscal-period.read",
    "ledger.fiscal-period.transition",
    "ledger.fx-rate.ingest",
    "ledger.fx-rate.manual.create",
    "ledger.fx-rate.read",
    "ledger.journal.post",
    "ledger.journal.read",
    "ledger.journal.reverse",
    "ledger.posting-rule.create",
    "ledger.posting-rule.delete",
    "ledger.posting-rule.preview",
    "ledger.posting-rule.read",
    "ledger.posting-rule.update",
    "ledger.report.read",
    "partners.channel_identity.read",
    "partners.customer_group.archive",
    "partners.customer_group.create",
    "partners.customer_group.read",
    "partners.customer_group.update",
    "partners.customer_profile.archive",
    "partners.customer_profile.read",
    "partners.customer_profile.update",
    "partners.groups.manage",
    "partners.parties.read",
    "partners.party.archive",
    "partners.party.create",
    "partners.party.pii.read",
    "partners.party.read",
    "partners.party.update",
    "partners.party_address.archive",
    "partners.party_address.create",
    "partners.party_address.read",
    "partners.party_address.update",
    "partners.party_contact.archive",
    "partners.party_contact.create",
    "partners.party_contact.read",
    "partners.party_contact.update",
    "partners.payment_terms.archive",
    "partners.payment_terms.create",
    "partners.payment_terms.read",
    "partners.payment_terms.update",
    "partners.supplier_group.archive",
    "partners.supplier_group.create",
    "partners.supplier_group.read",
    "partners.supplier_group.update",
    "partners.supplier_profile.archive",
    "partners.supplier_profile.read",
    "partners.supplier_profile.update",
    "partners.terms.manage",
    "platform.approval.decide",
    "platform.approval.delegate",
    "platform.approval.read",
    "platform.audit.read",
    "platform.branch.read",
    "platform.branch.write",
    "platform.notification.manage",
    "platform.notification.read",
    "platform.users.read",
    "purchasing.config.read",
    "purchasing.grn.post",
    "purchasing.grn.read",
    "purchasing.landed_cost.create",
    "purchasing.landed_cost.delete",
    "purchasing.landed_cost.post",
    "purchasing.landed_cost.read",
    "purchasing.landed_cost.update",
    "purchasing.purchase_order.close",
    "purchasing.purchase_order.create",
    "purchasing.purchase_order.delete",
    "purchasing.purchase_order.read",
    "purchasing.purchase_order.send",
    "purchasing.purchase_order.submit",
    "purchasing.purchase_order.transmission.read",
    "purchasing.purchase_order.update",
    "purchasing.reorder_suggestion.convert",
    "purchasing.reorder_suggestion.dismiss",
    "purchasing.reorder_suggestion.read",
    "purchasing.supplier_item.create",
    "purchasing.supplier_item.delete",
    "purchasing.supplier_item.read",
    "purchasing.supplier_item.update",
    "sales.order.pii.read",
    "sales.order.read",
  ],
  mfa_level: "none",
};

export const LOCKOUT_THRESHOLD = 5;
export const LOCKOUT_DURATION_MS = 15 * 60 * 1000;

interface MockAuthState {
  loggedInUserId: string | null;
  failedAttempts: number;
  lockedUntil: Date | null;
  /**
   * Whether an erp_session cookie may restore the session (mirrors the real
   * server-side session store). True on module init so a browser page reload
   * with a live cookie stays signed in; tests' reset() clears it so a
   * lingering cookie in the MSW jar cannot leak state between cases.
   */
  cookieSessionValid: boolean;
}

function initialAuthState(): MockAuthState {
  return { loggedInUserId: null, failedAttempts: 0, lockedUntil: null, cookieSessionValid: false };
}

export const db: { auth: MockAuthState; users: MockUser[] } = {
  auth: { ...initialAuthState(), cookieSessionValid: true },
  users: [DEMO_USER],
};

/** Restore pristine state (session, lockout counters). For tests. */
export function reset(): void {
  db.auth = initialAuthState();
  db.users = [DEMO_USER];
}

export function findUserByCode(code: string): MockUser | undefined {
  const needle = code.toLowerCase();
  return db.users.find((u) => u.code.toLowerCase() === needle);
}

export function loggedInUser(): MockUser | undefined {
  const id = db.auth.loggedInUserId;
  if (id === null) return undefined;
  return db.users.find((u) => u.id === id);
}

export function isLocked(now: Date = new Date()): boolean {
  return db.auth.lockedUntil !== null && db.auth.lockedUntil.getTime() > now.getTime();
}

/** Smaller POST /session authentication response. */
export function toLoginSessionUser(user: MockUser): LoginSessionUser {
  return {
    id: user.id,
    code: user.code,
    display_name: user.display_name,
    permissions: [...user.permissions],
    mfa_level: user.mfa_level,
    default_branch_id: user.default_branch_id,
    allowed_branch_ids: [...user.allowed_branch_ids],
  };
}

/** Canonical GET /session bootstrap response. */
export function toSessionUser(user: MockUser, actingBranchId: string | null): SessionUser {
  const activeAllowed = user.allowed_branch_ids
    .filter((branchId) =>
      BRANCHES.some((branch) => branch.id === branchId && branch.status === "active"),
    )
    .sort();
  return {
    ...toLoginSessionUser(user),
    company_id: user.company_id,
    // Every app, so the mocked shell behaves exactly as it did before apps
    // were installable. A test that wants an uninstalled app overrides this.
    installed_modules: [
      "core",
      "catalog",
      "partners",
      "accounting",
      "inventory",
      "tax",
      "receivables",
      "payables",
      "banking",
      "fixed_assets",
      "purchasing",
      "sales",
      "channels",
    ],
    allowed_branch_ids: activeAllowed,
    acting_branch_id: actingBranchId,
    company: {
      id: user.company_id,
      code: "ALWAHA",
      name: "Al Waha Furniture",
      name_ar: "الواحة للأثاث",
      base_currency: "SAR",
    },
  };
}
